// components/FeaturesSection.js
import { motion } from 'framer-motion';
import {
  FiZap,
  FiBell,
  FiSmartphone,
  FiLock,
  FiRefreshCcw,
  FiBarChart2,
  FiArrowRight
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import mockupImg from '../../assets/mkp.png';

const FeaturesSection = () => {
  const features = [
    {
      title: "Performance Otimizada",
      description: "Seu aplicativo carrega mais rápido que um navegador, proporcionando melhor experiência ao usuário.",
      Icon: FiZap,
      delay: 0.1
    },
    {
      title: "Notificações Push",
      description: "Mantenha seus usuários engajados com notificações personalizadas direto para seus dispositivos.",
      Icon: FiBell,
      delay: 0.2
    },
    {
      title: "Design Responsivo",
      description: "Layout perfeito em todos os dispositivos, garantindo a melhor experiência para seus usuários.",
      Icon: FiSmartphone,
      delay: 0.3
    },
    {
      title: "Segurança Máxima",
      description: "Proteção SSL integrada e atualizações automáticas de segurança para proteger seus dados.",
      Icon: FiLock,
      delay: 0.4
    },
    {
      title: "Atualizações Automáticas",
      description: "Seu app sempre atualizado sem necessidade de novas publicações nas lojas.",
      Icon: FiRefreshCcw,
      delay: 0.5
    },
    {
      title: "Analytics Integrado",
      description: "Acompanhe o desempenho do seu app com métricas detalhadas sobre o uso e engajamento.",
      Icon: FiBarChart2,
      delay: 0.6
    }
  ];

  const openWhatsApp = () => {
    const phoneNumber = "5582996992182"; 
    const message = "Olá!";
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
    }
  };

  return (
    <section id="features" className="py-24 lg:py-32 bg-white relative overflow-hidden border-t border-gray-50">
      {/* Background Pattern - Subtle diagonal stripes */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: `repeating-linear-gradient(45deg, #000 0, #000 1px, transparent 0, transparent 50%)`,
          backgroundSize: '10px 10px'
        }}
      ></div>
      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        
        <div className="text-center mb-16 lg:mb-24">
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 tracking-tight mb-4">
            Tudo que você precisa em um só lugar
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-lg font-light leading-relaxed">
            Nossa plataforma oferece todas as ferramentas profissionais para seu aplicativo decolar sem complicações.
          </p>
        </div>

        {/* Escolha Tecnológica Comparison */}
        <motion.div 
          className="mb-24 lg:mb-32"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-50px" }}
        >
          <div className="mb-10 flex flex-col items-center max-w-5xl mx-auto text-center">
            <span className="inline-block border border-gray-200 text-gray-600 bg-gray-50 px-3 py-1 text-xs font-bold tracking-widest uppercase rounded-full mb-4">
              A Escolha Tecnológica
            </span>
            <h3 className="text-2xl md:text-4xl font-extrabold text-gray-900 tracking-tight">
              O risco do app nativo tradicional
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto px-4">
            
            {/* Card App Nativo */}
            <div className="bg-white border border-gray-200 rounded-3xl p-8 lg:p-10 shadow-sm flex flex-col relative overflow-hidden transition-all hover:shadow-md">
              <h4 className="text-2xl font-bold text-gray-900 mb-3">App Nativo</h4>
              <p className="text-gray-500 text-sm leading-relaxed mb-8 min-h-[60px]">
                Totalmente dependente das lojas Google Play e Apple Store para cada atualização.
              </p>
              
              <ul className="space-y-5 flex-grow">
                {[
                  "Aprovação demorada (dias ou semanas)",
                  "Dependência total das lojas",
                  "Atualizações travadas por revisão",
                  "Usuário precisa baixar updates manualmente"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start text-sm text-gray-600 font-medium">
                    <span className="text-red-500 mr-3 mt-0.5 flex-shrink-0 bg-red-50 p-1 rounded-full">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Card App WebView Wrapper */}
            <div className="relative transform md:-translate-y-4 transition-all hover:-translate-y-5 h-full">
              {/* Badge Recomendado (Fora do card) */}
              <div className="absolute -top-3 right-8 bg-emerald-400 text-black text-[10px] sm:text-xs font-extrabold px-4 py-1.5 rounded uppercase tracking-widest shadow-lg z-20">
                Recomendado
              </div>
              
              {/* Card Content */}
              <div className="bg-[#050505] border-2 border-emerald-500 rounded-3xl p-8 lg:p-10 shadow-[0_20px_50px_rgba(16,185,129,0.15)] flex flex-col relative overflow-hidden h-full">
                <h4 className="text-2xl font-bold text-white mb-3 mt-2 relative z-10">App WebView</h4>
                <p className="text-gray-400 text-sm leading-relaxed mb-8 min-h-[60px] relative z-10">
                  Você controla tudo. Atualizações instantâneas, sem aprovação de loja.
                </p>
                
                <ul className="space-y-5 flex-grow relative z-10">
                  {[
                    "Atualização em tempo real",
                    "Independência total das lojas",
                    "Flexibilidade para qualquer mudança instantânea",
                    "Usuário sempre na versão mais recente"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start text-sm text-gray-200 font-medium">
                      <span className="text-emerald-400 mr-3 mt-0.5 flex-shrink-0 bg-emerald-400/10 p-1 rounded-full">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                
                {/* Background Glow */}
                <div className="absolute -top-10 -right-10 w-64 h-64 bg-emerald-500/20 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-indigo-500/10 rounded-full blur-[60px] pointer-events-none"></div>
              </div>
            </div>

          </div>
        </motion.div>

        {/* Layout Principal com Mockup */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Lado Esquerdo - Mockup */}
          <motion.div 
            className="lg:col-span-5 flex justify-center relative order-2 lg:order-1"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            {/* Efeitos de fundo do mockup */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-tr from-gray-100 to-white rounded-full blur-3xl opacity-60 pointer-events-none"></div>
            
            <img 
              src={mockupImg} 
              alt="Prévia do Aplicativo" 
              className="w-full max-w-[420px] relative z-10 drop-shadow-[0_20px_50px_rgba(0,0,0,0.15)]"
              loading="lazy"
            />
          </motion.div>

          {/* Lado Direito - Features Grid */}
          <motion.div 
            className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6 lg:gap-y-10 order-1 lg:order-2"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                variants={itemVariants}
                className="flex flex-col items-start text-left bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 lg:bg-transparent lg:p-0 lg:border-0 lg:shadow-none lg:hover:shadow-none lg:hover:border-transparent transition-all duration-300 group cursor-default"
              >
                <div className="w-10 h-10 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-900 mb-4 group-hover:bg-[#0a0a0a] group-hover:text-white transition-all duration-300 shadow-sm">
                  <feature.Icon className="w-5 h-5" />
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 mb-2 tracking-tight" translate="no">
                  {feature.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed font-light mb-4 flex-grow">
                  {feature.description}
                </p>
                
                <button 
                  onClick={openWhatsApp}
                  className="text-xs font-semibold text-gray-400 group-hover:text-gray-900 flex items-center transition-colors duration-300 cursor-pointer mt-auto"
                >
                  Saiba mais
                  <FiArrowRight className="w-3 h-3 ml-1 transition-transform group-hover:translate-x-1" />
                </button>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Call to action inferior */}
        <motion.div 
          className="text-center mt-20 flex justify-center order-3"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <button 
            onClick={openWhatsApp}
            className="px-8 py-3.5 bg-[#0a0a0a] text-white rounded-xl font-medium text-sm md:text-base transition-all duration-300 flex items-center justify-center gap-2 hover:bg-[#1a1a1a] shadow-md hover:shadow-lg hover:-translate-y-0.5 outline-none cursor-pointer"
          >
            <FaWhatsapp className="w-5 h-5 opacity-90" />
            <span>Falar com um especialista</span>
          </button>
        </motion.div>

      </div>
    </section>
  );
};

export default FeaturesSection;